import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { HOUSE_GRID, HOUSE_GROWN } from './house-config.js';
import {
  counter,
  gallery,
  hallDeck,
  hangingLantern,
  heartTree,
  heartTreeCrown,
  kitchenShelves,
  kitchenTable,
  ovalRug,
  playMat,
  playTent,
  stairs,
  starLight,
  stove,
  toyChest,
  toyBlocks,
  type Parts,
} from './house-models.js';
import { placeBeds, placed, type FloorSpot, type RoomKit, type RoomPlan } from './house-rooms.js';
import { inLane, loftWalk } from './house-walk.js';

/*
 * The grown rooms (#381 B3, the owner-approved mockup 2026-10-12): what the
 * Playroom, the Kitchen loft and the two-storey Heart Hall hold, registered
 * by look in house-rooms.ts. Each builder places its pieces from the room's
 * box (so a modest data edit to its size still fits), with every piece merged into
 * the room's one solid mesh and one glow mesh: furniture adds triangles,
 * never draw calls. The Keeper stays in the Snug, so these rooms plan only
 * where their residents stand.
 *
 * Pieces are built facing local -z, toward the open front: yaw 0 faces the
 * dollhouse camera, -π/2 stands against the west wall facing in, π/2 the east.
 *
 * The builders are function declarations: house-rooms.ts imports them and
 * this file imports `placed` back, and a declaration is there whichever
 * module loads first (a `const` would not be).
 */

const WEST = -Math.PI / 2;
const EAST = Math.PI / 2;

/** A room's helpers: `at(u, v)` is metres from its front-left corner, on its floor. */
function frame(kit: RoomKit) {
  const { box } = kit;
  return {
    w: box.x1 - box.x0,
    d: box.z1 - box.z0,
    y: box.y0,
    at: (u: number, v: number): [number, number] => [box.x0 + u, box.z0 + v],
    spot: (u: number, v: number): FloorSpot => ({ x: box.x0 + u, z: box.z0 + v }),
  };
}

/** Places a piece (solid meshes, or solid and glow parts) on the room's floor, turned by `yaw`. */
function put(
  kit: RoomKit,
  piece: Parts | readonly Mesh[],
  x: number,
  y: number,
  z: number,
  yaw: number,
): void {
  const parts: Parts = isParts(piece) ? piece : { solid: [...piece], glow: [] };
  if (parts.solid.length > 0) kit.solid([placed(parts.solid, x, y, z, yaw)]);
  if (parts.glow.length > 0) kit.glow([placed(parts.glow, x, y, z, yaw)]);
}

const isParts = (piece: Parts | readonly Mesh[]): piece is Parts => !Array.isArray(piece);

/**
 * Places a solid piece at the first of `spots` where it keeps out of the
 * room's doorway lanes (#381 W3), or at the last: where it went.
 */
function putClear(
  kit: RoomKit,
  make: () => readonly Mesh[],
  y: number,
  spots: readonly { x: number; z: number; yaw: number }[],
): { x: number; z: number; yaw: number } {
  for (const [i, s] of spots.entries()) {
    const mesh = placed(make(), s.x, y, s.z, s.yaw);
    mesh.computeWorldMatrix(true);
    const { minimumWorld: lo, maximumWorld: hi } = mesh.getBoundingInfo().boundingBox;
    if (i < spots.length - 1 && inLane(kit.lanes, { x0: lo.x, x1: hi.x, z0: lo.z, z1: hi.z }, y)) {
      mesh.dispose();
      continue;
    }
    kit.solid([mesh]);
    return s;
  }
  throw new Error('putClear: no spots');
}

/**
 * The Playroom (level 2): a play mat of coloured squares in the middle, a
 * toy chest with its lid open by the back wall, a striped play tent in the
 * back-right corner and a star night-light in the front corner. One bed along the back wall, the
 * next along the east wall under its window.
 */
export function playroomRoom(kit: RoomKit): RoomPlan {
  const { scene } = kit;
  const { w, d, y, at, spot } = frame(kit);

  const [mx, mz] = at(w * 0.45, d * 0.4);
  put(kit, playMat(scene), mx, y, mz, 0);

  // Off the back wall: the lid leans back and must stay clear of it.
  const [cx, cz] = at(0.85, d - 0.8);
  put(kit, toyChest(scene), cx, y, cz, 0);
  kit.shadow(cx, cz, 1.3, 0.9);

  const [tx, tz] = at(w - 0.95, d - 0.95);
  put(kit, playTent(scene), tx, y, tz, 0.4);
  kit.shadow(tx, tz, 1.7, 1.7);

  placeBeds(kit, [
    { ...spot(w * 0.49, d - 0.65), yaw: 0 },
    { ...spot(w - 0.62, d * 0.4), yaw: EAST },
  ]);

  // The star night-light in the open front-left corner, where it reads.
  const [lx, lz] = at(0.45, 0.45);
  put(kit, starLight(scene), lx, y, lz, 0);
  kit.pool(lx, lz, 0.9);
  kit.light('lamp', lx, y + 0.9, lz);

  const [bx, bz] = at(w * 0.66, 0.55);
  put(kit, toyBlocks(scene), bx, y, bz, 0.3);

  return {
    keeper: null,
    // B5a: toward the Snug's side, so a phone frame round the Snug holds all
    // of them (render check: every resident in view) a little closer in than B4's.
    residents: [spot(w * 0.22, d * 0.36), spot(w * 0.44, d * 0.46), spot(w * 0.26, d * 0.64)], // TUNE
  };
}

/**
 * The Kitchen loft (level 3, upstairs): a little iron stove in the back-left
 * corner with its pipe up to the roof, a counter along the back wall under
 * the window, shelves of jars on the west wall and a round table with a pie
 * in the middle. Its beds stand along the east wall.
 */
export function kitchenRoom(kit: RoomKit): RoomPlan {
  const { scene } = kit;
  const { w, d, y, at, spot } = frame(kit);

  // On the low tier the fire's window is bright vinyl in the room's solid
  // mesh: alone upstairs, its glow would cost the floor a draw call.
  const [sx, sz] = at(0.65, d - 0.55);
  const fire = stove(scene, HOUSE_GRID.storey - 0.2);
  put(
    kit,
    kit.tier === 'low' ? { solid: [...fire.solid, ...fire.glow], glow: [] } : fire,
    sx,
    y,
    sz,
    0,
  );
  kit.shadow(sx, sz, 1.3, 1.0);
  kit.pool(sx, sz - 0.6, 1.3);
  kit.light('fire', sx, y + 0.8, sz - 0.7);

  const [hx, hz] = at(0.27, d * 0.42);
  // Between the stove and the beds; a Kitchen tuned too narrow goes without.
  // A doorway in the back wall (onto the Heart Hall's landing, #381 W3)
  // keeps its lane clear: then it stands under the shelves on the west wall.
  const counterW = Math.min(2.0, w - 2.6);
  if (counterW >= 0.6) {
    const back = at(1.3 + counterW / 2, d - 0.45);
    const k = putClear(kit, () => counter(scene, counterW), y, [
      { x: back[0], z: back[1], yaw: 0 },
      { x: kit.box.x0 + HOUSE_GRID.wall / 2 + 0.36, z: hz, yaw: WEST },
    ]);
    const across = k.yaw === 0;
    kit.shadow(k.x, k.z, across ? counterW + 0.3 : 0.9, across ? 0.9 : counterW + 0.3);
  }

  put(kit, kitchenShelves(scene, Math.min(2.2, d * 0.5)), hx, y, hz, WEST);

  const [tx, tz] = at(w * 0.48, d * 0.4);
  put(kit, kitchenTable(scene), tx, y, tz, 0);
  kit.shadow(tx, tz, 2.2, 1.4);

  placeBeds(kit, [
    { ...spot(w - 0.62, d - 0.85), yaw: EAST },
    { ...spot(w - 0.62, 0.85), yaw: EAST },
  ]);

  return {
    keeper: null,
    // In front of the table and by the counter: the stools stand behind the table.
    residents: [spot(w * 0.3, d * 0.16), spot(w * 0.62, d * 0.16), spot(w * 0.5, d * 0.66)],
  };
}

/**
 * The Heart Hall (level 4, the top level, two storeys): the Heart Tree grows
 * up through the middle with a gold heart in its crown, a gallery runs along
 * the back wall at the second storey with stairs up the east wall, lanterns
 * hang either side of the tree and a gold-edged rug lies in front of it. The
 * beds sleep in a row under the gallery.
 */
export function heartHallRoom(kit: RoomKit): RoomPlan {
  const { scene, box } = kit;
  const { w, d, y, at, spot } = frame(kit);
  const g = HOUSE_GROWN.hall;
  const upper = HOUSE_GRID.storey;
  const tall = box.y1 - box.y0;
  const deck = g.galleryDepth;

  // The gallery along the back wall, its floor at the second storey.
  if (box.storeys > 1) {
    const inner = w - HOUSE_GRID.wall;
    const [gx, gz] = at(w / 2, d - HOUSE_GRID.wall / 2 - deck / 2);
    // Its front rail opens where the Keeper walks on and off it (W3): the
    // stairs' top and the walkway, as the walk lays them out.
    const openings: [number, number][] = [];
    // Stairs up the east wall to the gallery's front edge (a Hall tuned too
    // shallow for a walkable flight goes without).
    const front = d - HOUSE_GRID.wall / 2 - deck;
    const run = front - 0.6;
    if (run >= 2) {
      const [sx, sz] = at(w - g.stairWidth / 2 - HOUSE_GRID.wall / 2, front - run);
      kit.solid([placed(stairs(scene, g.stairWidth, upper, run, g.steps), sx, y, sz, 0)]);
      kit.shadow(sx, sz + run / 2, g.stairWidth + 0.3, run);
      openings.push([sx - g.stairWidth / 2 - gx, sx + g.stairWidth / 2 - gx]);
    }
    // The walkway down the west wall to an upstairs doorway (the Kitchen
    // loft's), as the walk's `loftWalk` lays it out.
    for (const walkway of loftWalk(box, kit.lanes).decks) {
      const parts = hallDeck(scene, walkway, y);
      kit.solid(parts.deck, true);
      kit.solid(parts.posts);
      if (walkway.z1 > box.z1 - HOUSE_GRID.wall / 2 - deck - 0.01)
        openings.push([walkway.x0 - gx, walkway.x1 - gx]);
    }
    kit.solid([placed(gallery(scene, inner, deck, upper, openings), gx, y + upper, gz, 0)]);
  }

  // The tree stands right of the middle: at w / 2 the Kitchen loft's wall hid its trunk.
  const treeU = w * 0.58;
  const [rx, rz] = at(treeU, d * 0.3);
  put(kit, ovalRug(scene, Math.min(2.2, w * 0.24), Math.min(1.3, d * 0.2)), rx, y, rz, 0);

  const treeH = Math.min(5.6, tall - 0.4);
  const [tx, tz] = at(treeU, d * 0.56);
  put(kit, heartTree(scene, treeH), tx, y, tz, 0);
  kit.shadow(tx, tz, 2.0, 2.0);
  // A warm light just in front of the crown's gold heart.
  const crown = heartTreeCrown(treeH);
  kit.light('lamp', tx, y + crown.y - 0.6, tz + crown.z - 1.0);

  // Either side of the tree's crown, clear of the gallery's posts.
  for (const u of [w * 0.32, w * 0.88]) {
    const [lx, lz] = at(u, d * 0.42);
    put(kit, hangingLantern(scene, 1.6), lx, y + tall - 0.1, lz, 0);
    // A warm pool under each (B5a: the hall read gloomy at night on high).
    kit.pool(lx, lz, HOUSE_GROWN.hall.lanternPool);
  }

  // Beds in a row under the gallery, either side of the tree: room for 5 (the
  // data has 4); a Hall tuned past that would want a second row.
  const n = kit.box.beds;
  const spacing = Math.min(1.7, (w - 1.0) / Math.max(1, n));
  placeBeds(
    kit,
    Array.from({ length: n }, (_, i) => {
      const side = i < Math.ceil(n / 2) ? -1 : 1;
      const k = side < 0 ? i : i - Math.ceil(n / 2);
      const u = side < 0 ? 0.5 + 0.75 + k * spacing : w - 0.5 - 0.75 - k * spacing;
      return { ...spot(u, d - 0.65), yaw: 0 };
    }),
  );

  // Residents stand right of the Kitchen loft, which hides the Hall's left
  // from the dollhouse camera (render check: faces hidden on the iPad's 48°
  // pitch). Past the band's four, more wait on the left, out of the way.
  const band = HOUSE_GROWN.hall.residentBand;
  const residents = Array.from({ length: Math.max(n, band.length) }, (_, i) => {
    const b = band[i];
    if (b) return spot(w * b[0], d * b[1]);
    const k = i - band.length;
    return spot(w * (0.1 + 0.1 * (k % 4)), d * (0.3 + 0.16 * Math.floor(k / 4)));
  });
  return { keeper: null, residents };
}
